import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR7_1RenovationOrDevelopment,
	isRenovationSectionB,
	isRenovationSectionA,
	isGR7_2RequestOccupation,
	isCase1SectionA,
	isCase3or5or6SectionA,
	isCase3SectionA,
	isCase4SectionA,
	isCase5SectionA,
	isCase6SectionA,
	isOccupationSectionA,
	isOccupationSectionB,
	isOccupationSectionC,
	isOccupationSectionD,
	isOccupationSectionE
} from '../__index.utils'

export const STEP4_2FIELDS = {
	typesOfWorkOccupation: 'typesOfWorkOccupation',
	projectedUseNewOccupation: 'projectedUseNewOccupation',
	projectedUseOccupation: 'projectedUseOccupation',
	currentUseOccupation: 'currentUseOccupation',
	additionalProjectedUseOccupation: 'additionalProjectedUseOccupation',
	conditionalProjectedUseOccupation: 'conditionalProjectedUseOccupation',
	occupantNameSectionA: 'occupantNameSectionA',
	occupiedAreaSectionA: 'occupiedAreaSectionA',
	// occupiedAreaUnitSectionA: 'occupiedAreaUnitSectionA',
	occupationActivityDescription: 'occupationActivityDescription',
	isLocalOrTerrain: 'isLocalOrTerrain',
	occupationActivityType: 'occupationActivityType',
	occupationActivityTypeDetails: 'occupationActivityTypeDetails',
	occupantNameSectionB: 'occupantNameSectionB',
	occupiedAreaSectionB: 'occupiedAreaSectionB',
	// occupiedAreaUnitSectionB: 'occupiedAreaUnitSectionB',
	conditionalUseAuthorizationRequest: 'conditionalUseAuthorizationRequest',
	conditionalUseNumber: 'conditionalUseNumber',
	executiveCommitteeResolutionNumber: 'executiveCommitteeResolutionNumber',
	startDateOccupation: 'startDateOccupation',
	endDateOccupation: 'endDateOccupation',
	isLocalModificationsRequired: 'isLocalModificationsRequired',
	isEnseigneInstallationPrevue: 'isEnseigneInstallationPrevue',
	isAlcoholPermitRequired: 'isAlcoholPermitRequired',
	alcoholPermitActivityType: 'alcoholPermitActivityType',
	alcoholPermitActivityTypeDetails: 'alcoholPermitActivityTypeDetails',
	isMunicipalCertificateRequired: 'isMunicipalCertificateRequired',
	isTerraceModificationRequired: 'isTerraceModificationRequired',
	isLocataireOccupation: 'isLocataireOccupation'
} as const

export type Step4_2Fields = keyof typeof STEP4_2FIELDS

/**
 * useStep4_2State - Custom hook to manage the state for step 4_2 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_2.
 */

// --- 4.2. State HOOK for Step 4.2 ---
// This hook is used to manage the state of Step 4.2 in the GR7
export function useStep4_2State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR7_2 = isGR7_2RequestOccupation(answers?.whatToDo)

	// SECTION A
	const occupationSectionA = isOccupationSectionA(answers.typesOfWorkOccupation)
	const case1SectionA = isCase1SectionA(answers.typesOfWorkOccupation)
	const case4SectionA = isCase4SectionA(answers.typesOfWorkOccupation)
	const case3or5or6SectionA = isCase3or5or6SectionA(
		answers.typesOfWorkOccupation
	)
	const case3SectionA = isCase3SectionA(answers.typesOfWorkOccupation)
	const case5SectionA = isCase5SectionA(answers.typesOfWorkOccupation)
	const case6SectionA = isCase6SectionA(answers.typesOfWorkOccupation)

	// SECTION B
	const occupationSectionB = isOccupationSectionB(answers.typesOfWorkOccupation)

	// SECTION C
	const occupationSectionC = isOccupationSectionC(answers.typesOfWorkOccupation)

	// SECTION D
	const occupationSectionD = isOccupationSectionD(answers.typesOfWorkOccupation)

	// SECTION E
	const occupationSectionE = isOccupationSectionE(answers.typesOfWorkOccupation)

	return {
		// Quel(s) type(s) de travaux souhaitez-vous effectuer?
		typesOfWorkOccupation: {
			name: 'typesOfWorkOccupation',
			label: pageAssets?.request_form_what_would_you_like_to_do,
			labelKey: 'request_form_what_would_you_like_to_do',
			value: answers?.typesOfWorkOccupation?.toString() || '',
			required: isGR7_2,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// ***************************************
		// Section A - typesOfWorkOccupation
		// ***************************************

		// Libellé "Usage projeté du nouveau local"
		projectedUseNewOccupation: {
			name: 'projectedUseNewOccupation',
			label: pageAssets?.request_form_projectedUseNewOccupation,
			labelKey: 'request_form_projectedUseNewOccupation',
			value: answers?.projectedUseNewOccupation?.toString() || '',
			required: isGR7_2 && case1SectionA,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Usage projeté du local"
		projectedUseOccupation: {
			name: 'projectedUseOccupation',
			label: pageAssets?.request_form_commercialPermit_projectedUse,
			labelKey: 'request_form_commercialPermit_projectedUse',
			value: answers?.projectedUseOccupation?.toString() || '',
			required: isGR7_2 && case3SectionA,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Usage actuel du local
		currentUseOccupation: {
			name: 'currentUseOccupation',
			label: pageAssets?.request_form_currentUseOccupation,
			labelKey: 'request_form_currentUseOccupation',
			value: answers?.currentUseOccupation?.toString() || '',
			required: isGR7_2 && (case4SectionA || case3or5or6SectionA),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Usage additionnel projeté du local
		additionalProjectedUseOccupation: {
			name: 'additionalProjectedUseOccupation',
			label: pageAssets?.request_form_additionalProjectedUseOccupation,
			labelKey: 'request_form_additionalProjectedUseOccupation',
			value: answers?.additionalProjectedUseOccupation?.toString() || '',
			required: isGR7_2 && case5SectionA,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Usage conditionnel projeté du local
		conditionalProjectedUseOccupation: {
			name: 'conditionalProjectedUseOccupation',
			label: pageAssets?.request_form_conditionalProjectedUseOccupation,
			labelKey: 'request_form_conditionalProjectedUseOccupation',
			value: answers?.conditionalProjectedUseOccupation?.toString() || '',
			required: isGR7_2 && case6SectionA,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ************************************************
		// Section A - sous section : description de l'occupation

		// Nom de l'occupant (nom de la personne ou de l'entreprise)
		occupantNameSectionA: {
			name: 'occupantNameSectionA',
			label: pageAssets?.request_form_occupantName,
			labelKey: 'request_form_occupantName',
			value: answers?.occupantNameSectionA?.toString() || '',
			required: isGR7_2 && occupationSectionA,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie occupée
		occupiedAreaSectionA: {
			name: 'occupiedAreaSectionA',
			label: pageAssets?.request_form_occupiedArea,
			labelKey: 'request_form_occupiedArea',
			value: answers?.occupiedAreaSectionA?.toString() || '',
			required: isGR7_2 && occupationSectionA,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		occupiedAreaUnitSectionA: {
			name: 'occupiedAreaUnitSectionA',
			label: pageAssets?.request_form_occupiedAreaUnit,
			labelKey: 'request_form_occupiedAreaUnit',
			value:
				answers?.occupiedAreaUnitSectionA?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Description des activités
		occupationActivityDescription: {
			name: 'occupationActivityDescription',
			label: pageAssets?.request_form_occupationActivityDescription,
			labelKey: 'request_form_occupationActivityDescription',
			value: answers?.occupationActivityDescription?.toString() || '',
			required: isGR7_2 && occupationSectionA,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ****************************************
		// *Section B - Obtenir un certificat d'occupation pour l'usage temporaire d'un local ou d'un terrain
		// *******************************************
		// L'usage temporaire souhaité est-il pour un local ou un terrain?
		isLocalOrTerrain: {
			name: 'isLocalOrTerrain',
			label: pageAssets?.request_form_isLocalOrTerrain,
			labelKey: 'request_form_isLocalOrTerrain',
			value: answers?.isLocalOrTerrain?.toString() || '',
			required: isGR7_2 && occupationSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Quel type d'activités souhaitez-vous effectuer?"
		occupationActivityType: {
			name: 'occupationActivityType',
			label: pageAssets?.request_form_occupationActivityType,
			labelKey: 'request_form_occupationActivityType',
			value: answers?.occupationActivityType?.toString() || '',
			required: isGR7_2 && occupationSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Veuillez préciser
		occupationActivityTypeDetails: {
			name: 'occupationActivityTypeDetails',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
			labelKey: 'request_form_occupationActivityTypeDetails',
			value: answers?.occupationActivityTypeDetails || '',
			required:
				isGR7_2 &&
				occupationSectionB &&
				answers?.occupationActivityType?.toString() ==
					REQUEST_CONFIGS_KEYS.occupationActivityType.autres,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Nom de l'occupant (nom de la personne ou de l'entreprise)
		occupantNameSectionB: {
			name: 'occupantNameSectionB',
			label: pageAssets?.request_form_occupantName,
			labelKey: 'request_form_occupantName',
			value: answers?.occupantNameSectionB?.toString() || '',
			required: isGR7_2 && occupationSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie occupée
		occupiedAreaSectionB: {
			name: 'occupiedAreaSectionB',
			label: pageAssets?.request_form_occupiedArea,
			labelKey: 'request_form_occupiedArea',
			value: answers?.occupiedAreaSectionB?.toString() || '',
			required: isGR7_2 && occupationSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		occupiedAreaUnitSectionB: {
			name: 'occupiedAreaUnitSectionB',
			label: pageAssets?.request_form_occupiedAreaUnit,
			labelKey: 'request_form_occupiedAreaUnit',
			value:
				answers?.occupiedAreaUnitSectionB?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ****************************************
		// *Section C - Ajouter un usage conditionnel à un local existant
		// *******************************************
		// Le projet a-t-il fait l’objet d’une demande d’autorisation d’usage conditionnel?
		conditionalUseAuthorizationRequest: {
			name: 'conditionalUseAuthorizationRequest',
			label: pageAssets?.request_form_conditionalUseAuthorizationRequest,
			labelKey: 'request_form_conditionalUseAuthorizationRequest',
			value: answers?.conditionalUseAuthorizationRequest?.toString() || '',
			required: isGR7_2 && occupationSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Numéro de UC (usage conditionnel)
		conditionalUseNumber: {
			name: 'conditionalUseNumber',
			label: pageAssets?.request_form_conditionalUseNumber,
			labelKey: 'request_form_conditionalUseNumber',
			value: answers?.conditionalUseNumber?.toString() || '',
			required: isGR7_2 && occupationSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Numéro de résolution du comité exécutif (CE)
		executiveCommitteeResolutionNumber: {
			name: 'executiveCommitteeResolutionNumber',
			label: pageAssets?.request_form_executiveCommitteeResolutionNumber,
			labelKey: 'request_form_executiveCommitteeResolutionNumber',
			value: answers?.executiveCommitteeResolutionNumber?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// ****************************************
		// *Section D - Dates d'occupation
		// *******************************************
		// Date de début
		startDateOccupation: {
			name: 'startDateOccupation',
			label: pageAssets?.request_form_startDateOccupation,
			labelKey: 'request_form_startDateOccupation',
			value: answers?.startDateOccupation?.toString() || '',
			required: isGR7_2 && occupationSectionD,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Date de fin
		endDateOccupation: {
			name: 'endDateOccupation',
			label: pageAssets?.request_form_endDateOccupation,
			labelKey: 'request_form_endDateOccupation',
			value: answers?.endDateOccupation?.toString() || '',
			required: isGR7_2 && occupationSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ****************************************
		// *Section E - Informations supplémentaires
		// *******************************************
		// Des modifications au local (rénovation ou aménagement) sont-elles requises?
		isLocalModificationsRequired: {
			name: 'isLocalModificationsRequired',
			label: pageAssets?.request_form_isLocalModificationsRequired,
			labelKey: 'request_form_isLocalModificationsRequired',
			value: answers?.isLocalModificationsRequired?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous faire l'installation d'enseigne(s)?
		isEnseigneInstallationPrevue: {
			name: 'isEnseigneInstallationPrevue',
			label: pageAssets?.request_form_isEnseigneInstallationPrevue,
			labelKey: 'request_form_isEnseigneInstallationPrevue',
			value: answers?.isEnseigneInstallationPrevue?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous l'obtention d'un permis d'alcool (R.A.C.J)
		isAlcoholPermitRequired: {
			name: 'isAlcoholPermitRequired',
			label: pageAssets?.request_form_isAlcoholPermitRequired,
			labelKey: 'request_form_isAlcoholPermitRequired',
			value: answers?.isAlcoholPermitRequired?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Pour quel(s) type(s) d'activités?
		alcoholPermitActivityType: {
			name: 'alcoholPermitActivityType',
			label: pageAssets?.request_form_alcoholPermitActivityType,
			labelKey: 'request_form_alcoholPermitActivityType',
			value: answers?.alcoholPermitActivityType?.toString() || '',
			required:
				isGR7_2 &&
				occupationSectionE &&
				answers?.isAlcoholPermitRequired?.toString() == 'true',
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Veuillez préciser
		alcoholPermitActivityTypeDetails: {
			name: 'alcoholPermitActivityTypeDetails',
			label: pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
			labelKey: 'request_form_alcoholPermitActivityTypeDetails',
			value: answers?.alcoholPermitActivityTypeDetails || '',
			required:
				isGR7_2 &&
				occupationSectionE &&
				answers?.alcoholPermitActivityType?.toString() ==
					REQUEST_CONFIGS_KEYS.alcoholPermitActivityType.autre,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous l'obtention d'une attestation municipale pour la vente de véhicules routiers (O.P.C.)?
		isMunicipalCertificateRequired: {
			name: 'isMunicipalCertificateRequired',
			label: pageAssets?.request_form_isMunicipalCertificateRequired,
			labelKey: 'request_form_isMunicipalCertificateRequired',
			value: answers?.isMunicipalCertificateRequired?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous faire l'ajout ou la modification d'une terrasse?
		isTerraceModificationRequired: {
			name: 'isTerraceModificationRequired',
			label: pageAssets?.request_form_isTerraceModificationRequired,
			labelKey: 'request_form_isTerraceModificationRequired',
			value: answers?.isTerraceModificationRequired?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Êtes-vous propriétaire ou locataire du local voulant être occupé?
		isLocataireOccupation: {
			name: 'isLocataireOccupation',
			label: pageAssets?.request_form_isOwnerOrTenantOccupation,
			labelKey: 'request_form_isOwnerOrTenantOccupation',
			value: answers?.isLocataireOccupation?.toString() || '',
			required: isGR7_2 && occupationSectionE,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
